import { ChevronLeft, ChevronRight, InfoOutlined } from "@mui/icons-material";
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  IconButton,
  InputLabel,
  TextField,
  Fade,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import { view } from "@risingstack/react-easy-state";
import { isValid, format } from "date-fns";
import _ from "lodash";
import React from "react";
import { Carousel } from "react-responsive-carousel";
import {
  ITEM_CARD_WRAPPER,
  PRODUCT_TITLE,
  UPLOAD_CAPTION,
  ITEM_CARD_DIVIDER,
  APPROVAL_CHIP,
  PENDING_CHIP,
  ITEM_CARD_LABEL,
  ITEM_CARD_VALUE,
  ITEM_CARD_SELECT_WRAPPER,
  ITEM_CARD_SELECT_MENU,
  PRODUCT_WRAPPER,
} from "../styles";
import UPLOAD_OUTLINE from "../../assets/icons/upload-outline.png";
import PRODUCT_PLACEHOLDER from "../../assets/images/product_placeholder.png";
import { ProductStore } from "../../store";
import { Spinner } from "../shared/Spinner";

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    placement="top-start"
    TransitionComponent={Fade}
    TransitionProps={{ timeout: 300 }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#0F3D61",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#0F3D61",
    borderRadius: "8px",
    fontSize: 12,
    lineHeight: "14px",
    color: "#FFFFFF",
    padding: "12px",
  },
}));

const ProductDetails = (props: any) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [productInfo, setProductInfo] = React.useState<any>({});

  React.useEffect(() => {
    if (!_.isEmpty(ProductStore.productDetails)) {
      setProductInfo(ProductStore.productDetails);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ProductStore?.productDetails]);

  const getImages = (imageArr: any[] = []) => {
    if (!_.isEmpty(imageArr) && imageArr.length > 1) {
      return (
        <Stack minWidth={126} height={186}>
          <Carousel
            showArrows={true}
            axis={"horizontal"}
            showThumbs={false}
            showIndicators={false}
            statusFormatter={(current, total) => `${current}/${total} Images`}
            renderArrowPrev={(onClickHandler, hasPrev, label) => (
              <IconButton
                aria-label="prev"
                onClick={onClickHandler}
                title={label}
                sx={{ width: 24, height: 24 }}
              >
                <ChevronLeft />
              </IconButton>
            )}
            renderArrowNext={(onClickHandler, hasNext, label) => (
              <IconButton
                aria-label="prev"
                onClick={onClickHandler}
                title={label}
                sx={{ width: 24, height: 24 }}
              >
                <ChevronRight />
              </IconButton>
            )}
          >
            {imageArr.map((image: any, index: number) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  maxWidth: 100,
                  height: 120,
                  backgroundImage: `url(${image}), url(${PRODUCT_PLACEHOLDER})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                }}
              ></div>
            ))}
          </Carousel>
        </Stack>
      );
    } else {
      if (!imageArr || imageArr.length === 0) {
        return (
          <Stack
            minWidth={126}
            height={186}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <img
              src={PRODUCT_PLACEHOLDER}
              style={{ maxHeight: 150 }}
              alt={"product_image"}
            />
          </Stack>
        );
      } else {
        return (
          <Stack
            minWidth={126}
            height={186}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <img
              src={imageArr[0]}
              style={{ maxWidth: 126, maxHeight: 150 }}
              alt={"product_image"}
            />
          </Stack>
        );
      }
    }
  };

  return (
    <Stack sx={PRODUCT_WRAPPER}>
      {isLoading ? (
        <Spinner />
      ) : (
        <Stack py={2}>
          <Card sx={ITEM_CARD_WRAPPER}>
            <CardContent sx={{ p: 0, flex: 1 }}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flex={1}
              >
                <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
                  <Typography sx={PRODUCT_TITLE}>
                    {productInfo?.titles[0] || ""}
                  </Typography>
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  flex={1}
                  spacing={2}
                >
                  <img src={UPLOAD_OUTLINE} alt="upload" height={24} />
                  <Stack direction={"row"} alignItems={"center"}>
                    <Typography sx={UPLOAD_CAPTION}>File:</Typography>&nbsp;
                    <Typography sx={{ ...UPLOAD_CAPTION, color: "#282C36" }}>
                      {productInfo?.fileName || ""}
                    </Typography>
                  </Stack>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    sx={ITEM_CARD_DIVIDER}
                  />
                  <Stack direction={"row"} alignItems={"center"}>
                    <Typography sx={UPLOAD_CAPTION}>Posted on:</Typography>
                    &nbsp;
                    <Typography sx={{ ...UPLOAD_CAPTION, color: "#282C36" }}>
                      {productInfo?.fileUploadDateTime &&
                      isValid(new Date(productInfo?.fileUploadDateTime))
                        ? format(
                            new Date(productInfo?.fileUploadDateTime),
                            "MMM dd, yyyy"
                          )
                        : ""}
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"}>
                    <Typography sx={UPLOAD_CAPTION}>by:</Typography>&nbsp;
                    <Typography sx={{ ...UPLOAD_CAPTION, color: "#282C36" }}>
                      {productInfo?.uploadedBy || ""}
                    </Typography>
                  </Stack>
                  {productInfo?.isApproved === true ? (
                    <Chip label="Approved" sx={APPROVAL_CHIP} />
                  ) : (
                    <Chip label="Ready for approval" sx={PENDING_CHIP} />
                  )}
                </Stack>
              </Stack>

              <Divider
                sx={{ background: "rgba(15, 61, 97, 0.08)", marginTop: 1 }}
              />

              <Stack direction={"row"} spacing={3} marginTop={1}>
                <Stack spacing={1}>{getImages(productInfo?.imageUrls)}</Stack>

                <Stack flex={1} spacing={0.5}>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    spacing={2}
                    flex={1}
                  >
                    <Stack
                      sx={{ borderRight: "1px solid #F1F2F3", width: "14%" }}
                    >
                      <Typography sx={ITEM_CARD_LABEL}>UPC</Typography>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography
                          sx={ITEM_CARD_VALUE}
                        >{`#${productInfo?.upc}`}</Typography>
                        <BootstrapTooltip
                          title={
                            <React.Fragment>
                              {productInfo?.source
                                .split(",")
                                ?.map((s: string, i: number) => (
                                  <Typography
                                    key={`${s}_${i}`}
                                    fontSize={"inherit"}
                                  >{`• ${s}`}</Typography>
                                ))}
                            </React.Fragment>
                          }
                        >
                          <IconButton
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{ paddingY: 0 }}
                          >
                            <InfoOutlined
                              sx={{
                                color: "#9DA1AE",
                                fontSize: 16,
                                "&:hover": {
                                  color: "#686C7E",
                                },
                              }}
                            />
                          </IconButton>
                        </BootstrapTooltip>
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{ borderRight: "1px solid #F1F2F3", width: "33%" }}
                    >
                      <Typography sx={ITEM_CARD_LABEL}>
                        Source - ItemCode
                      </Typography>
                      <Typography sx={ITEM_CARD_VALUE}>
                        {productInfo?.itemCode || ""}
                      </Typography>
                    </Stack>
                    <Stack
                      sx={{ borderRight: "1px solid #F1F2F3", width: "33%" }}
                    >
                      <Typography sx={ITEM_CARD_LABEL}>Brand</Typography>
                      <Typography sx={ITEM_CARD_VALUE}>
                        {productInfo?.brand || ""}
                      </Typography>
                    </Stack>
                    <Stack
                      sx={{ borderRight: "1px solid #F1F2F3", width: "10%" }}
                    >
                      <Typography sx={ITEM_CARD_LABEL}>Size</Typography>
                      <Typography sx={ITEM_CARD_VALUE}>
                        {productInfo?.size || ""}
                      </Typography>
                    </Stack>
                    {productInfo?.pack && (
                      <Stack sx={{ width: "10%" }}>
                        <Typography sx={ITEM_CARD_LABEL}>Pack</Typography>
                        <Typography sx={ITEM_CARD_VALUE}>
                          {productInfo?.pack || ""}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Stack
                    sx={ITEM_CARD_SELECT_WRAPPER}
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1.5}
                  >
                    <Stack flex={1}>
                      <InputLabel
                        htmlFor="product-title-select"
                        sx={ITEM_CARD_LABEL}
                      >
                        Product Title
                      </InputLabel>

                      <Stack direction={"row"} alignItems={"center"}>
                        <TextField
                          id="product-title-input"
                          label=""
                          variant="standard"
                          value={productInfo?.titles[0] || ""}
                          fullWidth
                          multiline
                          InputProps={{
                            sx: ITEM_CARD_SELECT_MENU,
                            readOnly: true,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack
                    sx={ITEM_CARD_SELECT_WRAPPER}
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1.5}
                  >
                    <Stack flex={1}>
                      <InputLabel
                        htmlFor="long-desc-select"
                        sx={ITEM_CARD_LABEL}
                      >
                        Long Description
                      </InputLabel>

                      <Stack direction={"row"} alignItems={"center"}>
                        <TextField
                          id="long-desc-input"
                          label=""
                          variant="standard"
                          value={productInfo?.lDescription || ""}
                          fullWidth
                          multiline
                          maxRows={4}
                          InputProps={{
                            sx: ITEM_CARD_SELECT_MENU,
                            readOnly: true,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack
                    sx={ITEM_CARD_SELECT_WRAPPER}
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1.5}
                  >
                    <Stack flex={1}>
                      <InputLabel htmlFor="ing-select" sx={ITEM_CARD_LABEL}>
                        Ingredients
                      </InputLabel>

                      <Stack direction={"row"} alignItems={"center"}>
                        <TextField
                          id="ing-input"
                          label=""
                          variant="standard"
                          value={productInfo?.ingredients || ""}
                          fullWidth
                          multiline
                          maxRows={4}
                          InputProps={{
                            sx: ITEM_CARD_SELECT_MENU,
                            readOnly: true,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Stack>
  );
};

export default view(ProductDetails);
